import { useTranslation } from "react-i18next";
import { AlertTriangle, PenLine, Search } from "lucide-react";

import type { JobRun } from "../types/job";
import { agentReportOf } from "../utils/runs";


interface JobRunReportProps {
  run: JobRun;
  /** No histórico: a lista de ações e o consumo, além do resumo e das pendências. */
  detailed?: boolean;
}

/** Resumo, pendências e o que o agente fez — no teste de um job e no histórico geral. */
export function JobRunReport({ run, detailed = false }: JobRunReportProps) {
  const { t } = useTranslation();
  const { report, pending } = agentReportOf(run);
  const actions = Array.isArray(report.actions) ? report.actions : [];
  const writes = typeof report.writes === "number" ? report.writes : 0;

  return (
    <div className="space-y-3 rounded-md border border-border/60 p-3 text-sm">
      {report.summary ? (
        <div>
          <p className="font-medium">{t("jobs.test.summary")}</p>
          <p className="whitespace-pre-wrap text-muted-foreground">{report.summary}</p>
        </div>
      ) : (
        <p className="text-muted-foreground">{t("jobs.test.noReport")}</p>
      )}

      {pending.length > 0 && (
        <div>
          <p className="font-medium">{t("jobs.test.pending")}</p>
          <ul className="list-disc pl-5 text-muted-foreground">
            {pending.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {detailed && actions.length > 0 && (
        <div>
          <p className="font-medium">{t("jobs.runs.actionsTitle")}</p>
          <ul className="mt-1 space-y-1">
            {actions.map((action, index) => (
              <li key={`${action.tool ?? "action"}-${index}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                {action.is_error ? (
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                ) : action.write ? (
                  <PenLine className="h-3.5 w-3.5 shrink-0 text-primary" />
                ) : (
                  <Search className="h-3.5 w-3.5 shrink-0" />
                )}
                <code className="truncate">{(action.tool ?? "").replace(/^mcp__prodgy__/, "")}</code>
                {action.is_error && <span className="text-destructive">{t("jobs.runs.actionFailed")}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {t("jobs.test.actions", { count: actions.length })}
        {" · "}
        {t("jobs.test.writes", { count: writes })}
        {detailed && report.usage && (
          <>
            {" · "}
            {t("jobs.runs.usage", {
              calls: report.usage.modelCalls ?? 0,
              tokens: (report.usage.inputTokens ?? 0) + (report.usage.outputTokens ?? 0),
            })}
          </>
        )}
      </p>
    </div>
  );
}
